// ─── TiposTransporteView — tabela editável de tipos de transporte (Casais) ──
// Criar / alterar / inativar tipos. Pré-carregada com os 10 tipos da CasaisHub.
// Usados nas linhas de transporte da viagem (OT de entrega) e no dashboard.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Tooltip, CircularProgress, Switch, Button, FormControlLabel,
} from '@material-ui/core';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import { usePlannerTheme, usePlannerDialogTheme, usePlannerToolbarTheme, brandGradient, ON_BRAND } from '../../theme/plannerTheme';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CategoryIcon from '@material-ui/icons/Category';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import { setActiveView } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surf-page, #f8fafc)', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', background: 'var(--surf-card, #fff)', borderBottom: '1px solid var(--surf-border, #e2e8f0)' },
  title: { fontSize: '1.05rem', fontWeight: 800, color: 'var(--surf-title, #0f172a)', display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)', marginTop: 2 },
  toolbar: { display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center', borderBottom: '1px solid var(--surf-border-soft, #f1f5f9)', background: 'var(--surf-card, #fff)' },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px', maxWidth: 760 },
  addRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 },
  headerIcon: { width: 34, height: 34, borderRadius: 10, background: '#ccfbf1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  row: { background: 'var(--surf-card, #fff)', borderRadius: 12, padding: '8px 12px 8px 16px', marginBottom: 9, border: '1px solid var(--surf-border, #e8edf3)', display: 'flex', alignItems: 'center', gap: 10, transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: '#99f6e4', boxShadow: '0 4px 14px rgba(13,148,136,0.08)' } },
  rowInactive: { opacity: 0.55, background: 'var(--surf-card-alt, #f8fafc)' },
  idx: { width: 26, fontSize: '0.72rem', fontWeight: 700, color: 'var(--surf-muted2, #94a3b8)', textAlign: 'center' },
  empty: { padding: '80px 24px', textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
}));

function TiposTransporteView({ lang = 'pt' }) {
  const classes = useStyles();
  const theme = usePlannerTheme();
  const newUI = usePlannerToolbarTheme() !== 'classic'; // identidade ecooGPS só em Claro/Escuro
  const grad = brandGradient(theme);
  const dlgTheme = usePlannerDialogTheme();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [newName, setNewName] = useState('');
  const [edits, setEdits] = useState({});       // id → nome em edição
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const qs = showInactive ? '?includeInactive=1' : '';
      const r = await fetch(`/planner-api/transport-types${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setTypes(await r.json());
    } catch (e) {
      setError(pt ? 'Não foi possível carregar os tipos.' : 'Could not load types.');
    } finally { setLoading(false); }
  }, [token, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function addType() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      const r = await fetch('/planner-api/transport-types', { method: 'POST', headers, body: JSON.stringify({ name }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      setNewName(''); await fetchAll();
    } catch (e) { setError(e.message); }
  }

  async function saveName(t) {
    const value = edits[t.id] !== undefined ? edits[t.id].trim() : t.name;
    if (!value || value === t.name) { setEdits(prev => { const n = { ...prev }; delete n[t.id]; return n; }); return; }
    setSavingId(t.id); setError(null);
    try {
      const r = await fetch(`/planner-api/transport-types/${t.id}`, { method: 'PUT', headers, body: JSON.stringify({ name: value }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      setTypes(prev => prev.map(x => x.id === t.id ? { ...x, name: value } : x));
      setEdits(prev => { const n = { ...prev }; delete n[t.id]; return n; });
      setSavedId(t.id); setTimeout(() => setSavedId(cur => cur === t.id ? null : cur), 1500);
    } catch (e) { setError(e.message); } finally { setSavingId(null); }
  }

  async function toggleActive(t) {
    setError(null);
    try {
      const r = await fetch(`/planner-api/transport-types/${t.id}`, { method: 'PUT', headers, body: JSON.stringify({ isActive: !t.isActive }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) { setError(e.message); }
  }

  async function seedDefaults() {
    setError(null);
    try {
      const r = await fetch('/planner-api/transport-types/seed-defaults', { method: 'POST', headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setTypes(await r.json());
    } catch (e) { setError(e.message); }
  }

  return (
    <ThemeProvider theme={dlgTheme}>
    <Box className={classes.root} data-planner-theme={theme}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}><ArrowBackIcon style={{ fontSize: 20 }} /></IconButton>
        </Tooltip>
        <Box style={{ flex: 1 }}>
          <Typography className={classes.title}><Box component="span" className={classes.headerIcon} style={newUI ? { background: grad, boxShadow: '0 6px 16px rgba(24,189,238,0.34)' } : undefined}><CategoryIcon style={{ fontSize: 19, color: newUI ? ON_BRAND : '#0d9488' }} /></Box> {pt ? 'Tipos de Transporte' : 'Transport Types'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Tabela editável — criar, alterar e inativar tipos usados nas viagens' : 'Editable table — create, edit and deactivate types'}</Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}><IconButton size="small" onClick={fetchAll} disabled={loading}><RefreshIcon style={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <FormControlLabel
          control={<Switch size="small" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />}
          label={<Typography style={{ fontSize: '0.75rem', color: 'var(--surf-text, #475569)' }}>{pt ? 'Mostrar inativos' : 'Show inactive'}</Typography>} />
      </Box>

      <Box className={classes.list}>
        {error && <Box style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: '0.78rem' }}>{error}</Box>}

        <Box className={classes.addRow}>
          <TextField size="small" variant="outlined" fullWidth
            placeholder={pt ? 'Novo tipo de transporte…' : 'New transport type…'}
            value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addType(); }} />
          <Button onClick={addType} variant="contained" disableElevation startIcon={<AddIcon />}
            style={{ background: newUI ? grad : '#0d9488', color: newUI ? ON_BRAND : '#fff', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {pt ? 'Adicionar' : 'Add'}
          </Button>
        </Box>

        {loading && types.length === 0 ? (
          <Box className={classes.empty}><CircularProgress size={32} style={{ color: '#0d9488' }} /></Box>
        ) : types.length === 0 ? (
          <Box className={classes.empty}>
            {newUI
              ? <Box style={{ width: 66, height: 66, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surf-head-bg, #f1f6f9)', color: theme === 'dark' ? '#6f93a6' : '#94a3b8' }}><CategoryIcon style={{ fontSize: 32 }} /></Box>
              : <CategoryIcon style={{ fontSize: 48, color: '#cbd5e1' }} />}
            <Typography style={{ fontWeight: 700, color: 'var(--surf-title, #0f172a)' }}>{pt ? 'Ainda não há tipos de transporte.' : 'No transport types yet.'}</Typography>
            <Button onClick={seedDefaults} variant="outlined" style={{ textTransform: 'none' }}>
              {pt ? 'Carregar 10 tipos por defeito' : 'Load 10 default types'}
            </Button>
          </Box>
        ) : types.map((t, i) => {
          const val = edits[t.id] !== undefined ? edits[t.id] : t.name;
          const dirty = val !== t.name;
          return (
            <Box key={t.id} className={`${classes.row} ${!t.isActive ? classes.rowInactive : ''}`}>
              <Typography className={classes.idx}>{i + 1}</Typography>
              <TextField size="small" variant="outlined" fullWidth
                value={val}
                onChange={e => setEdits(prev => ({ ...prev, [t.id]: e.target.value }))}
                onBlur={() => saveName(t)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
              {savingId === t.id ? <CircularProgress size={18} />
                : savedId === t.id ? <CheckIcon style={{ fontSize: 20, color: '#16a34a' }} />
                : dirty ? <Button size="small" onClick={() => saveName(t)} style={{ textTransform: 'none', fontSize: '0.72rem', color: '#0d9488' }}>{pt ? 'Guardar' : 'Save'}</Button>
                : <Box style={{ width: 24 }} />}
              <Tooltip title={t.isActive ? (pt ? 'Ativo — clicar para inativar' : 'Active') : (pt ? 'Inativo — clicar para ativar' : 'Inactive')}>
                <Switch size="small" color="primary" checked={t.isActive} onChange={() => toggleActive(t)} />
              </Tooltip>
              <Tooltip title={pt ? 'Inativar' : 'Deactivate'}>
                <span>
                  <IconButton size="small" onClick={() => toggleActive(t)} disabled={!t.isActive}><DeleteIcon style={{ fontSize: 16, color: t.isActive ? '#ef4444' : '#cbd5e1' }} /></IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default TiposTransporteView;
